import ViewWrapper from '@/components/ViewWrapper.vue';
import { adminGuard } from '@/utils/adminGuard';
import { trackPageView } from '@/utils/analytics';
import { getI18n } from '@/utils/i18nAccessor';
import * as i18nFormat from '@/utils/i18nFormat';
import { defineAsyncComponent, h } from 'vue';
import { createRouter, createWebHistory } from 'vue-router';
import { refreshToken } from '../services/authService';
import store from '../store';

// Async component loader with improved options for mobile compatibility
const asyncComponent = (component) => {
	return defineAsyncComponent({
		loader: component,
		delay: 200,
		timeout: 15000, // Increased from 5s to 15s for mobile Safari compatibility
		errorComponent: {
			render() {
				try {
					const i18n = getI18n();
					const t = i18n?.global?.t || ((k)=>k);
					return h('div', { class: 'async-error-fallback', style: 'padding: 20px; text-align: center;' }, [
						h('p', t('app.error')),
						h('button', {
							onclick: () => window.location.reload(),
							style: 'padding: 8px 16px; margin-top: 10px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;'
						}, t('app.refresh'))
					]);
				} catch {
					return h('div', { class: 'async-error-fallback', style: 'padding: 20px; text-align: center;' }, [
						h('p', 'An error occurred while loading the component. Try refreshing the page.'),
						h('button', { onclick: () => window.location.reload(), style: 'padding: 8px 16px; margin-top: 10px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;' }, 'Refresh')
					]);
				}
			}
		},
		loadingComponent: {
			render() {
				try {
					const i18n = getI18n();
					const t = i18n?.global?.t || ((k)=>k);
					return h('div', { class: 'async-loading-fallback', style: 'padding: 20px; text-align: center;' }, [
						h('div', { style: 'display: inline-block; width: 20px; height: 20px; border: 3px solid #f3f3f3; border-top: 3px solid #007bff; border-radius: 50%; animation: spin 1s linear infinite;' }),
						h('p', { style: 'margin-top: 10px;' }, t('app.loading')),
						h('style', null, '@keyframes spin {0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }')
					]);
				} catch {
					return h('div', { class: 'async-loading-fallback', style: 'padding: 20px; text-align: center;' }, [
						h('div', { style: 'display: inline-block; width: 20px; height: 20px; border: 3px solid #f3f3f3; border-top: 3px solid #007bff; border-radius: 50%; animation: spin 1s linear infinite;' }),
						h('p', { style: 'margin-top: 10px;' }, 'Loading...'),
						h('style', null, '@keyframes spin {0% { transform: rotate(0deg);} 100% { transform: rotate(360deg);} }')
					]);
				}
			}
		},
		suspensible: false // Prevent suspense-related issues on mobile
	});
};

// Route for llms.txt
const llmsRoute = {
	path: '/llms.txt',
	name: 'LLMsConfig',
	component: {
		render() {
			// This will serve the static file directly
			return null;
		}
	},
	beforeEnter: () => {
		// Redirect to the actual static file
		window.location.href = '/llms.txt';
	}
};

const routes = [
	llmsRoute,
	{
		path: '/',
		name: 'PublicHome',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'PublicHomeView',
				component: asyncComponent(() => import('@/views/PublicHomeView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.publicHome' }
			}
		]
	},
	// Persona Management Routes
	{
		path: '/personas',
		name: 'Personas',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'PersonaListView',
				component: asyncComponent(() => import('@/views/PersonaListView.vue')),
				meta: { requiresAuth: true, titleKey: 'routes.personas' }
			}
		]
	},
	{
		path: '/personas/create',
		name: 'PersonaCreate',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'PersonaCreationView',
				component: asyncComponent(() => import('@/views/PersonaCreationView.vue')),
				meta: { requiresAuth: true, titleKey: 'routes.createPersona' }
			}
		]
	},
	{
		path: '/personas/:token/memories',
		name: 'PersonaMemories',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'PersonaMemoriesView',
				component: asyncComponent(() => import('@/views/PersonaSettingsView.vue')),
				meta: { requiresAuth: true, titleKey: 'routes.editPersona' }
			}
		]
	},
	{
		path: '/personas/:token/settings',
		name: 'PersonaSettings',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'PersonaSettingsView',
				component: asyncComponent(() => import('@/views/PersonaSettingsView.vue')),
				meta: { requiresAuth: true, titleKey: 'routes.editPersona' }
			}
		]
	},
	{

		path: '/privacy-policy',
		name: 'PrivacyPolicy',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'PrivacyPolicyView',
				component: asyncComponent(() => import('@/views/policy/PrivacyPolicyView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.privacyPolicy' }
			}
		]
	},
	{
		path: '/terms',
		name: 'TermsAndConditions',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'TermsAndConditionsView',
				component: asyncComponent(() => import('@/views/policy/TermsAndConditionsView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.terms' }
			}
		]
	},
	{
		path: '/data-removal',
		name: 'DataRemovalRequest',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'DataRemovalRequestView',
				component: asyncComponent(() => import('@/views/policy/DataRemovalRequestView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.dataRemoval' }
			}
		]
	},
	/*
	{
		path: '/version-history',
		alias: '/changelog',
		name: 'VersionHistory',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'VersionHistoryView',
				component: asyncComponent(() => import('@/views/VersionHistoryView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.versionHistory' }
			}
		]
	}, */
	// Admin Panel Routes - Lazy loaded with admin guard
	{
		path: '/admin',
		name: 'Admin',
		component: asyncComponent(() => import('@/layouts/AdminLayout.vue')),
		meta: { requiresAuth: true, requiresAdmin: true },
		children: [
			{
				path: '',
				redirect: '/admin/dashboard'
			},
			{
				path: 'dashboard',
				name: 'AdminDashboard',
				component: asyncComponent(() => import('@/views/admin/AdminDashboardView.vue')),
				meta: { requiresAuth: true, requiresAdmin: true, titleKey: 'routes.adminDashboard' }
			},
			{
				path: 'users',
				name: 'AdminUsers',
				component: asyncComponent(() => import('@/views/admin/AdminUsersView.vue')),
				meta: { requiresAuth: true, requiresAdmin: true, titleKey: 'routes.adminUsers' }
			},
			{
				path: 'analytics',
				name: 'AdminAnalytics',
				component: asyncComponent(() => import('@/views/admin/AdminAnalyticsView.vue')),
				meta: { requiresAuth: true, requiresAdmin: true, titleKey: 'routes.adminAnalytics' }
			}
		]
	},
	{
		path: '/token-test',
		name: 'TokenTest',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'TokenTestView',
				component: asyncComponent(() => import('@/views/TokenTestView.vue')),
				meta: { requiresAuth: true, titleKey: 'routes.tokenTest' }
			}
		]
	},
	{
		path: '/subscriptionresult',
		name: 'SubscriptionResult',
		component: asyncComponent(() => import('@/layouts/ChatLayout.vue')),
		children: [
			{
				path: '',
				name: 'SubscriptionResultView',
				component: asyncComponent(() => import('@/views/SubscriptionResultView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.subscriptionResult' }
			}
		],
	},
	{
		path: '/support',
		name: 'Support',
		alias: '/destek',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'SupportView',
				component: asyncComponent(() => import('@/views/SupportView.vue')),
				meta: { requiresAuth: false, showSupport: true, titleKey: 'routes.support' }
			}
		]
	},
	{
		path: '/chat',
		redirect: '/personas'
	},
	{
		path: '/chat/c/:characterToken',
		name: 'CharacterChatView',
		component: asyncComponent(() => import('@/layouts/ChatLayout.vue')),
		children: [
			{
				path: '',
				name: 'CharacterChatDetailView',
				component: {
					setup() {
						return () => h(ViewWrapper, () => [
							h(asyncComponent(() => import('@/views/PersonaChatView.vue')))
						]);
					}
				},
				meta: { requiresAuth: false, titleKey: 'routes.chat' }
			}
		]
	},
	{
		path: '/login',
		alias: '/verify/:token',
		name: 'Login',
		component: asyncComponent(() => import('@/layouts/AuthLayout.vue')),
		children: [
			{
				path: '',
				name: 'LoginConsentView',
				component: asyncComponent(() => import('@/views/LoginView.vue')),
				meta: { requiresAuth: false, showConsent: true, titleKey: 'routes.login' }
			}
		],
	},
	{
		path: '/loginwithfacebook',
		name: 'LoginWithFacebook',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'LoginWithFacebookView',
				component: asyncComponent(() => import('@/views/PublicHomeView.vue')),
				meta: { requiresAuth: false, showConsent: true, loginWithFacebook: true, titleKey: 'routes.login' }
			}
		],
	},
	{
		path: '/register',
		name: 'Register',
		component: asyncComponent(() => import('@/layouts/AuthLayout.vue')),
		children: [
			{
				path: '',
				name: 'RegisterView',
				component: asyncComponent(() => import('@/views/RegisterView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.register' }
			}
		],
	},
	{
		path: '/reset-password',
		name: 'ResetPassword',
		component: asyncComponent(() => import('@/layouts/AuthLayout.vue')),
		children: [
			{
				path: '',
				name: 'ResetPasswordView',
				component: asyncComponent(() => import('@/views/ResetPasswordView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.resetPassword' }
			}
		],
	},
	{
		path: '/profile',
		name: 'Profile',
		component: asyncComponent(() => import('@/layouts/DashboardLayout.vue')),
		children: [
			{
				path: '',
				name: 'ProfileView',
				component: {
					setup() {
						return () => h(ViewWrapper, () => [
							h(asyncComponent(() => import('@/views/ProfileView.vue')))
						]);
					}
				},
				meta: { requiresAuth: true, titleKey: 'routes.profile' }
			},
		],
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: asyncComponent(() => import('@/layouts/PublicLayout.vue')),
		children: [
			{
				path: '',
				name: 'NotFoundView',
				component: asyncComponent(() => import('@/views/NotFoundView.vue')),
				meta: { requiresAuth: false, titleKey: 'routes.notFound' }
			}
		]
	},
	/* {
		path: '/workbook',
		name: 'WorkBook',
		component: asyncComponent(() => import('@/views/WorkBook.vue')),
		meta: { requiresAuth: true }
	}, */
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	base: '/console',
	routes,
});

// Auto-recover from stale chunk errors and async component timeouts
router.onError((err) => {
	const msg = (err && err.message) || '';
	const isChunkError =
		/chunk load/i.test(msg) ||
		/CSS_CHUNK_LOAD_FAILED/i.test(msg) ||
		/Loading CSS chunk/i.test(msg) ||
		/Loading chunk \d+ failed/i.test(msg);
	
	const isAsyncTimeout = /Async component timed out/i.test(msg);
	
	// Handle chunk loading errors with reload
	if (isChunkError) {
		const key = 'pvy_chunk_reload';
		if (!sessionStorage.getItem(key)) {
			sessionStorage.setItem(key, '1');
			const url = new URL(window.location.href);
			url.searchParams.set('v', Date.now().toString());
			window.location.replace(url.toString());
		}
	}
	
	// Handle async component timeouts with retry logic
	if (isAsyncTimeout) {
		const retryKey = 'pvy_async_retry';
		const retryCount = parseInt(sessionStorage.getItem(retryKey) || '0');
		
		if (retryCount < 2) { // Allow up to 2 retries
			sessionStorage.setItem(retryKey, (retryCount + 1).toString());
			console.log(`Async component timeout, retrying... (${retryCount + 1}/2)`);
			
			// Add a small delay before retry to help with mobile Safari
			setTimeout(() => {
				const url = new URL(window.location.href);
				url.searchParams.set('retry', Date.now().toString());
				window.location.replace(url.toString());
			}, 1000);
		} else {
			// After 2 retries, clear the retry counter and show error
			sessionStorage.removeItem(retryKey);
			console.error('Async component failed after retries, showing error page');
		}
	}
});

router.beforeEach(async (to, from, next) => {
	const isAuthRequired = to.matched.some(record => record.meta.requiresAuth);
	const isAdminRequired = to.matched.some(record => record.meta.requiresAdmin);
	
	// We will now attempt to fetch user data for all routes
	// to determine auth status globally. Redirection to /login
	// will only occur if authentication is required by the route and fails.
	
	// If navigating to login page, check if already logged in
	if (to.path === '/login') {
		try {
			// Try to fetch user data silently
			const response = await fetch('/api/user/me', { credentials: 'include' });
			
			if (response.ok) {
				// User is already logged in, so redirect to the main app page
				const user = await response.json();
				store.dispatch('setUser', user);
				next('/personas'); // Redirect to a dashboard or home page
				return;
			}
		} catch (error) {
			// If there's an error (e.g., network issue), it's safer to proceed to login
			console.warn('Auth check on login route failed, proceeding to login page:', error);
		}
		
		// If user is not logged in, or if the check fails, proceed to login page
		sessionStorage.setItem('on_login_page', 'true');
		sessionStorage.removeItem('auth_check_in_progress');
		sessionStorage.removeItem('logging_out');
		sessionStorage.removeItem('auth_failed');
		
		next();
		return;
	} else if (to.path === '/workbook') {
				// Localized workbook title using i18n and i18n-aware date formatting
				try {
					const i18n = getI18n();
					const t = i18n?.global?.t || ((k)=>k);
					const formattedDate = i18nFormat.formatDate(new Date(), { year: 'numeric', month: '2-digit', day: '2-digit' });
					document.title = t('routes.workbookTitle', { date: formattedDate });
				} catch {
					const fallbackDate = new Date().toISOString().slice(0, 10);
					document.title = `Current Sales Report - ${fallbackDate} `;
				}
		(function() {
			var link = document.querySelector("link[rel*='icon']") || document.createElement('link');
			link.type = 'image/x-icon';
			link.rel = 'shortcut icon';
			link.href = '/icons8-excel-32.png';
			document.getElementsByTagName('head')[0].appendChild(link);
		})();
		next();
		return;
	} else {
		// We're not on login page anymore
		sessionStorage.removeItem('on_login_page');
		
		// If we're navigating from login page to another page, skip the auth check
		if (from.path === '/login') {
			next();
			return;
		}
	}
	
	// Track if we're already in the auth check process to prevent loops
	const authCheckInProgress = sessionStorage.getItem('auth_check_in_progress');
	
	// If we're already checking auth, don't do it again to avoid infinite loops
	if (authCheckInProgress === 'true') {
		console.log('Auth check already in progress, preventing loop');
		// Break the cycle: redirect to login if auth is required and not already on login, otherwise proceed.
		if (isAuthRequired && to.path !== '/login') {
			next('/login');
		} else {
			next(); // Allow proceeding to public page or login page
		}
		return;
	}
	
	// Set flag to indicate we're checking auth
	sessionStorage.setItem('auth_check_in_progress', 'true');
	
	// Try to get user data with current token
	try {
		const response = await fetch('/api/user/me', {
			credentials: 'include' // Include cookies
		});
		
		// Clear the auth check flag
		sessionStorage.removeItem('auth_check_in_progress');
		
		// If successful, update user and continue
		if (response.ok) {
			const data = await response.json();
			store.dispatch('setUser', data);
			
			// Check admin privileges if required
			if (isAdminRequired) {
				adminGuard(to, from, next);
				return;
			}
			
			next();
			return;
		}
		
		// If 401 Unauthorized, try to refresh the token once
		if (response.status === 401) {
			console.log('Router guard: 401 response, attempting token refresh');
			
			// Try to refresh token
			const refreshSuccessful = await refreshToken();
			
			if (refreshSuccessful) {
				console.log('Router guard: Token refresh successful, retrying');
				
				// If refresh succeeded, try fetching user data again
				const retryResponse = await fetch('/api/user/me', {
					credentials: 'include'
				});
				
				if (retryResponse.ok) {
					const data = await retryResponse.json();
					store.dispatch('setUser', data);
					
					// Check admin privileges if required
					if (isAdminRequired) {
						adminGuard(to, from, next);
						return;
					}
					
					next();
					return;
				}
			}
			
			console.log('Router guard: Token refresh failed or fetch was unsuccessful. User will be cleared. Redirecting to login if auth is required.');
		}
		
		// If authentication failed (initial fetch, or after token refresh):
		// Clear user state. Redirect to login only if auth is required for the current route.
		store.dispatch('clearUser');
		if (isAuthRequired) {
			next('/login');
		} else {
				next(); // Allow proceeding to public page
		}
	} catch (error) {
		console.error('Authentication error:', error);
		// Clear the auth check flag
		sessionStorage.removeItem('auth_check_in_progress');
		// In case of any other error during authentication:
		// Clear user state. Redirect to login only if auth is required for the current route.
		store.dispatch('clearUser');
		if (isAuthRequired) {
			next('/login');
		} else {
			next(); // Allow proceeding to public page
		}
	}
});


// Track pageviews after each navigation and localize title
router.afterEach((to) => {
	try {
		const i18n = getI18n();
		const t = i18n?.global?.t || ((k)=>k);
		const metaTitleKey = to.matched.find(r => r.meta && r.meta.titleKey)?.meta?.titleKey;
		if (metaTitleKey) {
			document.title = t(metaTitleKey);
		}
	} catch (e) {
		// ignore title errors
	}
	try {
		trackPageView(to.name || to.path, {
			page_path: to.fullPath
		});
	} catch (error) {
		console.warn('Analytics pageview tracking error:', error);
	}
});

export default router;
